import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/common/Bits";
import { fmtDateTime, exportCSV } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollText, Search, Download } from "lucide-react";
import { toast } from "sonner";

const ALL = "__all__";
const ENTITY_TYPES = ["initiative", "risk_assessment", "initiative_control", "evidence_document", "committee_vote", "committee_decision", "shadow_detection", "reevaluation", "value_metric", "task", "session", "user", "audit_export"];

export default function Audit() {
  const { can } = useAuth();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [etype, setEtype] = useState(ALL);

  const load = () => {
    const params = {};
    if (q) params.q = q;
    if (etype !== ALL) params.entity_type = etype;
    api.get("/audit", { params }).then(({ data }) => setRows(data));
  };
  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, etype]);

  const doExport = async () => {
    try {
      await api.post("/audit/export");
      exportCSV(rows.map((r) => ({ fecha: r.timestamp, usuario: r.user, accion: r.action, tipo: r.entity_type, entidad: r.entity, motivo: r.reason, correlacion: r.correlation_id, origen: r.origin })), "auditoria_ia_ucchristus.csv");
      toast.success("Evidencia de auditoría exportada · la exportación quedó registrada");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Centro de auditoría" subtitle="Bitácora inmutable de eventos: sesiones, cambios, evaluaciones, votos, decisiones y exportaciones, con valores antes/después." testId="audit-header">
        {can.admin && (
          <Button variant="outline" onClick={doExport} disabled={!rows?.length} className="border-border" data-testid="audit-export-btn">
            <Download className="h-4 w-4 mr-2" /> Exportar evidencias
          </Button>
        )}
      </PageHeader>

      <div className="flex flex-wrap gap-2 mb-6">
        <div className="relative flex-1 min-w-56">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por usuario, acción o entidad…" className="pl-9 bg-card" data-testid="audit-search" />
        </div>
        <Select value={etype} onValueChange={setEtype}>
          <SelectTrigger className="w-56 bg-card" data-testid="audit-entity-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Tipo de entidad: todos</SelectItem>
            {ENTITY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {!rows ? <div className="space-y-2">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}</div> :
        rows.length === 0 ? <EmptyState icon={ScrollText} title="Sin eventos" /> : (
          <div className="relative pl-5 before:absolute before:left-1.5 before:top-2 before:bottom-2 before:w-px before:bg-border" data-testid="audit-timeline">
            {rows.map((r) => (
              <div key={r.id} className="relative pb-4 last:pb-0">
                <span className="absolute -left-5 top-2 h-3 w-3 rounded-full border-2 border-background bg-primary" />
                <div className="bg-card rounded-xl border border-border shadow-card p-4 ml-1">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold">{r.action}</p>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-secondary-foreground font-mono">{r.entity_type}</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground">{fmtDateTime(r.timestamp)} · #{r.correlation_id}</p>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{r.user} · {r.entity}{r.reason ? ` · Motivo: ${r.reason}` : ""}</p>
                  {(r.before || r.after) && (
                    <div className="grid sm:grid-cols-2 gap-2 mt-2 text-[11px]">
                      {r.before && <div className="rounded-lg bg-risk-high/5 border border-risk-high/15 p-2"><span className="font-semibold text-risk-high">Antes: </span>{JSON.stringify(r.before)}</div>}
                      {r.after && <div className="rounded-lg bg-accent/5 border border-accent/15 p-2"><span className="font-semibold text-accent">Después: </span>{JSON.stringify(r.after)}</div>}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
